import { JsonPipe, NgIf } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatListOption } from '@angular/material/list';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import {
  IAmanoOneAccessGroups,
  IAmanoOneAccessgroupUpdateResponse,
  IAmanoOneAccessHolderUpdateResponse,
  IAmanoOneLotConfigurationSettings,
  IAmanoOne_AccessGroupsWithMembershipResponse,
  IAmanoOne_AccessGroup,
  IAmanoOneCredentialUpdateResponse,
  IAmanoOneCredentialTypeResponse,
  IAmanoOneCredentialSystemCodeResponse,
} from 'src/app/shared/code/interfaces/parcs-interfaces';
import {
  IAmanoOneAddExistingParkerRequest,
  IAmanoParkerDataForCardReassignmentResponse,
  IParkerProfileDetail,
} from 'src/app/shared/interfaces';
import { MatSelectionListChange } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { TooltipPosition, MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DialogAmanoParcsAddNewAccessholderComponent } from 'src/app/core/dialog/dialog-amano-parcs-add-new-accessholder/dialog-amano-parcs-add-new-accessholder.component';
import { DialogCardNumberReassignComponent } from 'src/app/core/dialog/dialog-card-number-reassign/dialog-card-number-reassign.component';
import { MatDialog } from '@angular/material/dialog';
import { DataService } from 'src/app/core/services/data/data.service';

@Component({
  selector: 'app-amano-one-parker-settings',
  standalone: true,
  imports: [
    MatCardModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatInputModule,
    MatSelectModule,
    MatListModule,
    MatListOption,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './amano-one-parker-settings.component.html',
  styleUrl: './amano-one-parker-settings.component.scss',
  providers: [ParcsDataService],
})
export class AmanoOneParkerSettingsComponent implements OnInit {
  amanoOneConfigSettings: any;
  disableAddAccessGroup: boolean = true;
  disableRemoveAccessGroup: boolean = true;
  modeSettingValue: string = '';
  statusSettingValue: string = '';
  selectedSourceAccessGroup: string = '';
  selectedDestinationAccessGrouop: string = '';
  accessGroupUpdateInProgress: boolean = true;
  accessGroupLists: IAmanoOne_AccessGroupsWithMembershipResponse = {} as IAmanoOne_AccessGroupsWithMembershipResponse;
  amanoOneAccessGroups: IAmanoOne_AccessGroup[] = [];
  amanoOneAccessGroupsMemberOf: IAmanoOne_AccessGroup[] = [];
  amanoOneAccessHolder: IAmanoOneAccessHolderUpdateResponse = {} as IAmanoOneAccessHolderUpdateResponse;
  modeUpdateInProgress: boolean = false;
  statusUpdateInProgress: boolean = false;
  hasAccessHolder: boolean = false;
  accessHolderTracking: IAmanoOneAccessHolderUpdateResponse | null = null;
  newAccessHolderDialogHeight: string = '490px';
  newAccessHolderDialogWidth: string = '600px';
  facilityId: string = '';
  credentialTypeSettingValue: string = '';
  credentialCodeSettingValue: string = '';
  credentialTypeDisabled: boolean = false;
  matchingParcsCardNumberExist: boolean = false;
  credentialTypeList: IAmanoOneCredentialTypeResponse[] = [];
  systemCodeList: IAmanoOneCredentialSystemCodeResponse[] = [];
  isCredentialSaveDisabled: boolean = true;

  //Services
  #_appGeneralStateService = inject(AppGeneralStateService);
  private readonly _formBuilder = inject(FormBuilder);
  #_dialogService = inject(MessageDialogService);
  #_parcsDataService = inject(ParcsDataService);
  #_destroyRefService = inject(DestroyRef);
  #_matDialogService = inject(MatDialog);
  #_dataService = inject(DataService);

  readonly amanoEditForm = this._formBuilder.group({
    // accessGroup: '',
    // mode: '',
    status: '',
  });

  _parkerDetail: IParkerProfileDetail;
  @Input() set parkerDetail(value: IParkerProfileDetail) {
    this._parkerDetail = value;
    this.amanoOneConfigSettings = JSON.parse(
      this.#_appGeneralStateService.currentLotParcsConfiguration.settings.toLowerCase(),
    );
    // The settings JSON is parsed lowercased, so every key is lowercase - facilityId would be undefined.
    this.facilityId = this.amanoOneConfigSettings.facilityid;
    this.initSettingsData();
  }

  constructor() {
    this.#_appGeneralStateService.hasLinkedParcsAccount = undefined;
  }

  ngOnInit(): void {}

  onSubmitAmanoOneData(): void {
    // Logic to handle form submission
  }

  async initSettingsData(): Promise<void> {
    this.credentialTypeList = await this.getCredentialTypesListForLotNumber(
      this.#_appGeneralStateService.currentLotNumber,
    );

    this.accessGroupUpdateInProgress = true;
    this.#_appGeneralStateService.hasLinkedParcsAccount = undefined;
    const status: boolean = await this.getAmanoOneAccessHolderData(
      this._parkerDetail.parkerId,
      this.#_appGeneralStateService.currentLotNumber,
    );

    if (status) {
      this.hasAccessHolder = true;
      this.getAccessControlGroupLists(this._parkerDetail.parkerId, this.#_appGeneralStateService.currentLotNumber);
      this.newAccessHolderDialogHeight = '690px';
      this.newAccessHolderDialogWidth = '1200px';
      this.initStatusValue();
      this.initModeValue();
    } else {
      this.accessHolderTracking = await this.getAmanoOneAccessHolderTrackingData(
        this._parkerDetail.lotNumber,
        this._parkerDetail.cardNumber,
      );
      this.hasAccessHolder = false;
      this.newAccessHolderDialogHeight = '500px';
      this.newAccessHolderDialogWidth = '800px';
    }
    this.setParcsMatchingCardNumberStatus();
  }

  onResetAmanoOneEditForm(): void {}

  async setParcsMatchingCardNumberStatus(): Promise<void> {
    let cardNumbers: IAmanoOneCredentialUpdateResponse[] = this.amanoOneAccessHolder.credentials;
    this.matchingParcsCardNumberExist = false;

    for (let ii = 0; ii < cardNumbers.length; ii++) {
      const trimmedCredentialID = cardNumbers[ii].credentialReference?.trim() || '';

      if (cardNumbers[ii].credentialReference?.trim().includes(String(this._parkerDetail.cardNumber).trim())) {
        this.matchingParcsCardNumberExist = true;
        this.credentialCodeSettingValue = cardNumbers[ii].systemCode;
        console.log('CredentialCodeSetting', this.credentialCodeSettingValue);
        const credentialId: number = cardNumbers[ii].credentialType;
        if (credentialId == 6) {
          this.credentialTypeSettingValue = 'Barcode';
        } else if (credentialId == 7) {
          this.credentialTypeSettingValue = 'AVI/Prox/Magstripe';
          this.systemCodeList = await this.getCredentialSystemCodesListForLotNumber(
            this.#_appGeneralStateService.currentLotNumber,
            credentialId,
          );
        } else {
          this.credentialCodeSettingValue = '';
        }
        // Falsy check catches null, undefined, and empty string, all of which the API can return for "no system code".
        if (!this.credentialCodeSettingValue) {
          this.credentialCodeSettingValue = 'None';
        }
        return;
      }
    }
  }

  onUpdateCredentials(): void {
    let credentialId: number = 0;

    if (this.credentialTypeSettingValue === 'AVI/Prox/Magstripe') {
      credentialId = 7;
    } else if (this.credentialTypeSettingValue === 'Barcode') {
      credentialId = 6;
    }

    this.updateAmanoOneAccessHolderCredentials(
      this.#_appGeneralStateService.currentLotNumber,
      this._parkerDetail.parkerId,
      credentialId,
      this.credentialCodeSettingValue,
    );
  }

  onStatusChange(event: any) {
    this.updateAmanoOneAccessHolderStatusSetting(
      this.amanoOneAccessHolder.accessHolderID,
      this.statusSettingValue,
      this.#_appGeneralStateService.currentLotNumber,
    );
  }

  onModeChange(event: any) {
    this.updateAmanoOneAccessHolderModeSetting(
      this.amanoOneAccessHolder.accessHolderID,
      this.modeSettingValue,
      this.#_appGeneralStateService.currentLotNumber,
    );
  }

  onAccessGroupChange(event: MatSelectionListChange) {
    if (event.options && event.options.length > 0) {
      this.disableAddAccessGroup = false;
      this.disableRemoveAccessGroup = true;
    } else {
      this.disableAddAccessGroup = false;
      this.disableRemoveAccessGroup = false;
    }
  }

  onCurrentMemberAccessGroupChange(event: any) {
    if (event.options && event.options.length > 0) {
      this.disableAddAccessGroup = true;
      this.disableRemoveAccessGroup = false;
    } else {
      this.disableAddAccessGroup = false;
      this.disableRemoveAccessGroup = false;
    }
  }

  evaluateCredentialSaveStatus(): void {
    this.isCredentialSaveDisabled = false;

    if (this.credentialTypeSettingValue === '') {
      this.isCredentialSaveDisabled = true;
      return;
    }

    if (this.credentialTypeSettingValue === 'AVI/Prox/Magstripe' && this.credentialCodeSettingValue === '') {
      this.isCredentialSaveDisabled = true;
      return;
    }
  }

  displaySelectionChangedMessage(value: string): void {
    this.#_dialogService.snackBarTopCenterTimed(`The ${value} has been successfully updated.`, 3000);
  }

  onAddNewAccessGroup(): void {
    if (this.selectedSourceAccessGroup != '') {
      this.disableAddAccessGroup = true;
      this.disableRemoveAccessGroup = true;

      var parkerId = this._parkerDetail.parkerId;
      var lotNumber = this.#_appGeneralStateService.currentLotNumber;

      this.addNewAmanoOneAccessGroupId(parkerId, lotNumber, this.selectedSourceAccessGroup);
    }
  }

  onRemoveAccessGroup(): void {
    if (this.selectedDestinationAccessGrouop != '') {
      this.disableAddAccessGroup = true;
      this.disableRemoveAccessGroup = true;

      var parkerId = this._parkerDetail.parkerId;
      var lotNumber = this.#_appGeneralStateService.currentLotNumber;

      this.removeAmanoOneAccessGroupId(parkerId, lotNumber, this.selectedDestinationAccessGrouop);
    }
  }

  initModeValue(): void {
    var modeId = this.amanoOneAccessHolder.mode;

    switch (modeId) {
      case 1:
        this.modeSettingValue = 'Normal';
        break;
      case 2:
        this.modeSettingValue = 'Locked';
        break;
      case 3:
        this.modeSettingValue = 'Override';
        break;
      case 4:
        this.modeSettingValue = 'Override AP';
        break;
      case 5:
        this.modeSettingValue = 'Inactive';
        break;
      case 6:
        this.modeSettingValue = 'AP Inactive';
        break;
      default:
        this.modeSettingValue = '';
    }
  }

  initStatusValue(): void {
    var statusId: number = this.amanoOneAccessHolder.status;

    switch (statusId) {
      case 1:
        this.statusSettingValue = 'In';
        break;
      case 2:
        this.statusSettingValue = 'Out';
        break;
      case 3:
        this.statusSettingValue = 'Neutral';
        break;
      default:
        this.statusSettingValue = '';
    }
  }

  async getCredentialTypesListForLotNumber(lotNumber: string): Promise<IAmanoOneCredentialTypeResponse[]> {
    let credentialList: IAmanoOneCredentialTypeResponse[];

    try {
      credentialList = await firstValueFrom(this.#_parcsDataService.AmanoOne_CredentialType_GetList(lotNumber));
    } catch (err) {
      credentialList = null;
    }
    return credentialList !== null ? credentialList : null;
  }

  async getCredentialSystemCodesListForLotNumber(
    lotNumber: string,
    credentialTypeId: number,
  ): Promise<IAmanoOneCredentialSystemCodeResponse[]> {
    let systemCodes: IAmanoOneCredentialSystemCodeResponse[];

    try {
      systemCodes = await firstValueFrom(
        this.#_parcsDataService.AmanoOne_CredentialSystemCode_GetList(lotNumber, credentialTypeId),
      );
    } catch (err) {
      systemCodes = null;
    }
    return systemCodes !== null ? systemCodes : null;
  }

  async onAddParkerToAmanoOne(): Promise<void> {
    let enterAnimationDuration: string = '0ms';
    let exitAnimationDuration: string = '0ms';
    let reassignCardNumber: boolean = false;

    // Before adding this parker to Amano One, check whether the card number is already assigned to an
    // existing Amano One access holder. If it is, prompt the user to confirm reassigning the card number.
    // If the user declines, abort the add so the card number is not reassigned.
    const cardNumber: string = this._parkerDetail.cardNumber;
    const accessHolderId: string = await this.getAmanoOneAccessHolderForCardNumberLotNumber(
      this._parkerDetail.lotNumber,
      cardNumber,
    );

    if (accessHolderId !== '') {
      const amanoParkerData: IAmanoParkerDataForCardReassignmentResponse | null =
        await this.getAmanoOneParkerDataForCardReassignment(accessHolderId);

      if (amanoParkerData) {
        const confirmed: boolean = await this.displayDialogCardNumberReassign(amanoParkerData);
        if (!confirmed) {
          // User declined to reassign the card number; abort adding this parker to Amano One.
          return;
        } else {
          reassignCardNumber = true;
        }
      }
    }

    const accessHolderGroups: IAmanoOneAccessGroups[] = await this.getAllAmanoOneAccessHolderGroups();
    const dialogRef = this.#_matDialogService.open(DialogAmanoParcsAddNewAccessholderComponent, {
      data: {
        lotNumber: this._parkerDetail.lotNumber,
        cardNumber: this._parkerDetail.cardNumber,
        parkerId: this._parkerDetail.parkerId,
        facilityId: this.amanoOneConfigSettings.facilityid,
        accessHolder: this.accessHolderTracking,
      },
      hasBackdrop: true,
      width: this.newAccessHolderDialogWidth,
      maxWidth: this.newAccessHolderDialogWidth,
      height: this.newAccessHolderDialogHeight,
      maxHeight: this.newAccessHolderDialogHeight,
      disableClose: false,
      //
      panelClass: 'custom-dialog-container',
      enterAnimationDuration,
      exitAnimationDuration,
      position: {
        top: '200px',
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result != undefined) {
        const status = await this.onAddNewAmanoOneAccessHolder(
          this._parkerDetail.lotNumber,
          this._parkerDetail.parkerId,
          result,
          reassignCardNumber,
          this._parkerDetail.cardNumber,
        );

        if (status) {
          this.#_dialogService.snackBarTopCenterTimed('This Parker has been successfully added to Amano One.', 4000);
          this.initSettingsData();
        } else {
          this.#_dialogService.snackBarTopCenterTimed(
            'An error has occurred and the Parker was not added to Amano One. Please try again.',
            4000,
          );
        }
      }
    });
  }

  // Returns true if the user confirmed the card number reassignment, false otherwise.
  async displayDialogCardNumberReassign(
    amanoParkerData: IAmanoParkerDataForCardReassignmentResponse,
  ): Promise<boolean> {
    const parkerName: string = amanoParkerData.lastName + ', ' + amanoParkerData.firstName;
    // Use the card number on the parker detail; the API response omits cardNumber when the
    // Amano access holder has no associated spACE-AR parker.
    const cardNumber: string = this._parkerDetail.cardNumber;
    const parkerId: string = amanoParkerData.parkerId;
    // Guard against a null/undefined status so a missing 'active' value is treated as inactive instead of throwing.
    const isActive: boolean = (amanoParkerData.active ?? '').toUpperCase() == 'ACTIVE';

    const dialogRef = this.#_matDialogService.open(DialogCardNumberReassignComponent, {
      width: '650px',
      data: {
        parkerName,
        cardNumber,
        parkerId,
        isActive,
      },
    });

    const result: boolean = (await firstValueFrom(dialogRef.afterClosed())) === true;
    console.log(result ? 'User confirmed card reassignment.' : 'User canceled card reassignment.');

    return result;
  }

  /// Gets the accessHolderId for a given lot number and card number.
  // Used to check if an Amano One access holder already exists for the card number being added.
  async getAmanoOneAccessHolderForCardNumberLotNumber(lotNumber: string, cardNumber: string): Promise<string> {
    try {
      return await firstValueFrom(
        this.#_dataService.getAmanoOneAccessHolderforCardNumberLotNumberForUpdate(
          this._parkerDetail.parkerId,
          lotNumber,
          cardNumber,
        ),
      );
    } catch (err) {
      return '';
    }
  }

  async getAmanoOneParkerDataForCardReassignment(
    accessHolderId: string,
  ): Promise<IAmanoParkerDataForCardReassignmentResponse | null> {
    try {
      return await firstValueFrom(this.#_dataService.getAmanoOneParkerDataForCardReassignment(accessHolderId));
    } catch (err) {
      return null;
    }
  }

  async onAddNewAmanoOneAccessHolder(
    lotNumber: string,
    parkerId: string,
    amanoData: IAmanoOneAddExistingParkerRequest,
    reassignCardNumber: boolean,
    cardNumber: string,
  ): Promise<boolean> {
    const status: boolean = await firstValueFrom(
      this.#_parcsDataService.postNewAmanoOneParcsAccesHolderForExistingParker(
        lotNumber,
        parkerId,
        amanoData.accessGroupId,
        amanoData.credentialType,
        amanoData.credentialCode,
        reassignCardNumber,
        cardNumber,
      ),
    );
    return status;
  }

  addNewAmanoOneAccessGroupId(parkerId: string, lotNumber: string, accessGroupId: string): void {
    var accessGroups: IAmanoOneAccessgroupUpdateResponse[];
    this.accessGroupUpdateInProgress = true;

    this.#_parcsDataService
      .addNewAmanoOneParcsAccessGroupId(parkerId, lotNumber, accessGroupId)
      .pipe(takeUntilDestroyed(this.#_destroyRefService))
      .subscribe({
        next: (res) => {
          this.#_dialogService.snackBarTopCenterTimed(
            'The new Access Group has been successfully added to this Parker.',
            4000,
          );
        },
        error: (err: any) => {
          this.accessGroupUpdateInProgress = false;
          this.#_dialogService.snackBarTopCenterTimed(
            'An error has occurred and the Access Group was not added. Please try again.',
            4000,
          );
        },
        complete: () => {
          this.getAccessControlGroupLists(parkerId, lotNumber);
          this.accessGroupUpdateInProgress = false;
        },
      });
  }

  removeAmanoOneAccessGroupId(parkerId: string, lotNumber: string, accessGroupId: string): void {
    var accessGroups: IAmanoOneAccessgroupUpdateResponse[];
    this.accessGroupUpdateInProgress = true;

    this.#_parcsDataService
      .removeAmanoOneParcsAccessGroupId(parkerId, lotNumber, accessGroupId)
      .pipe(takeUntilDestroyed(this.#_destroyRefService))
      .subscribe({
        next: (res) => {
          this.#_dialogService.snackBarTopCenterTimed(
            'The new Access Group has been successfully removed from this Parker.',
            4000,
          );
        },
        error: (err: any) => {
          this.accessGroupUpdateInProgress = false;
          this.#_dialogService.snackBarTopCenterTimed(
            'An error has occurred and the Access Group was not removed. Please try again.',
            4000,
          );
        },
        complete: () => {
          // Update the access group lists
          this.getAccessControlGroupLists(parkerId, lotNumber);
        },
      });
  }

  async getAmanoOneAccessHolderTrackingData(
    lotNumber: string,
    cardNumber: string,
  ): Promise<IAmanoOneAccessHolderUpdateResponse | null> {
    let accessHolder: IAmanoOneAccessHolderUpdateResponse;

    try {
      accessHolder = await firstValueFrom(
        this.#_dataService.getAmanoOneParcsTrackingAccessHolder(lotNumber, cardNumber),
      );
    } catch (err) {
      accessHolder = null;
    }
    return accessHolder !== null ? accessHolder : null;
  }

  async getAmanoOneAccessHolderData(parkerId: string, lotNumber: string): Promise<boolean> {
    let accessHolder: IAmanoOneAccessHolderUpdateResponse;
    let status: boolean = true;

    try {
      accessHolder = await firstValueFrom(
        this.#_parcsDataService.getAmanoOneParcsAccessHolderByParkerId(parkerId, lotNumber),
      );
      this.amanoOneAccessHolder = accessHolder;
      this.#_appGeneralStateService.hasLinkedParcsAccount = true;
    } catch (err) {
      status = false;
      this.#_appGeneralStateService.hasLinkedParcsAccount = false;
    }

    return status;
  }

  async getAllAmanoOneAccessHolderGroups(): Promise<IAmanoOneAccessGroups[]> {
    let accessHolderGroups: IAmanoOneAccessGroups[];
    let status: boolean = true;
    const facilityId = this.amanoOneConfigSettings.facilityid;

    try {
      accessHolderGroups = await firstValueFrom(
        this.#_parcsDataService.getAmanoOneParcsAccessGroupsByFacilityId(facilityId),
      );
    } catch (err) {
      status = false;
    }
    return accessHolderGroups;
  }

  updateAmanoOneAccessHolderStatusSetting(accessHolderId: string, status: string, lotNumber: string): void {
    var accessGroups: IAmanoOneAccessgroupUpdateResponse[];

    this.statusUpdateInProgress = true;
    const currentStatus = this.statusSettingValue;

    this.#_parcsDataService
      .updateAmanoOneParcsStatusSetting(accessHolderId, status, lotNumber)
      .pipe(takeUntilDestroyed(this.#_destroyRefService))
      .subscribe({
        next: (res) => {
          this.#_dialogService.snackBarTopCenterTimed(
            "The Access Holder's Status setting has been successfully updated.",
            4000,
          );
          this.statusSettingValue = currentStatus;
        },
        error: (err: any) => {
          this.statusUpdateInProgress = false;
          this.#_dialogService.snackBarTopCenterTimed(
            'An error has occurred and the Access Holder Status setting was not updated. Please try again.',
            4000,
          );
        },
        complete: () => {
          this.statusUpdateInProgress = false;
        },
      });
  }

  updateAmanoOneAccessHolderCredentials(
    lotNumber: string,
    parkerId: string,
    credentialTypeId: number,
    systemCode: string,
  ): void {
    var accessGroups: IAmanoOneAccessgroupUpdateResponse[];

    this.statusUpdateInProgress = true;
    const currentStatus = this.statusSettingValue;

    this.#_parcsDataService
      .updateAmanoOneParcsAccessHolderCredentialSettings(lotNumber, parkerId, credentialTypeId, systemCode)
      .pipe(takeUntilDestroyed(this.#_destroyRefService))
      .subscribe({
        next: (res) => {
          this.#_dialogService.snackBarTopCenterTimed(
            "The Access Holder's Credential Setting has been successfully updated.",
            4000,
          );
          this.statusSettingValue = currentStatus;
        },
        error: (err: any) => {
          this.statusUpdateInProgress = false;
          this.#_dialogService.snackBarTopCenterTimed(
            'An error has occurred and the Access Holder Credential setting was not updated. Please try again.',
            4000,
          );
        },
        complete: () => {
          this.statusUpdateInProgress = false;
        },
      });
  }

  updateAmanoOneAccessHolderModeSetting(accessHolderId: string, mode: string, lotNumber: string): void {
    var accessGroups: IAmanoOneAccessgroupUpdateResponse[];

    this.modeUpdateInProgress = true;
    const currentMode = this.modeSettingValue;

    this.#_parcsDataService
      .updateAmanoOneParcsModeSetting(accessHolderId, mode, lotNumber)
      .pipe(takeUntilDestroyed(this.#_destroyRefService))
      .subscribe({
        next: (res) => {
          this.#_dialogService.snackBarTopCenterTimed(
            "The Access Holder's Mode setting has been successfully updated.",
            4000,
          );
          this.modeSettingValue = currentMode;
        },
        error: (err: any) => {
          this.modeUpdateInProgress = false;
          this.#_dialogService.snackBarTopCenterTimed(
            'An error has occurred and the Access Holder Mode setting was not updated. Please try again.',
            4000,
          );
        },
        complete: () => {
          this.modeUpdateInProgress = false;
        },
      });
  }

  // Gets two lists of access groups
  // First List is the list of Groups this access holder is a member of
  // Second list is the domain of all groups except for groups the access holder is a member of
  getAccessControlGroupLists(parkerId: string, lotNumber: string): void {
    var accessGroups: IAmanoOneAccessgroupUpdateResponse[];
    this.#_parcsDataService
      .getAmanoOneParcsAccessGroupsLists(parkerId, lotNumber)
      .pipe(takeUntilDestroyed(this.#_destroyRefService))
      .subscribe({
        next: (res) => {
          this.accessGroupLists = res;
        },
        error: (err: any) => {
          this.accessGroupUpdateInProgress = false;
          this.#_dialogService.snackBarTopCenterTimed(
            'An error has occurred and the Access Holder Membership lists could not be found. Please close spACE AR and try again.',
            4000,
          );
        },
        complete: () => {
          accessGroups = this.amanoOneAccessHolder.accessGroups;
          // this.initStatusValue();
          // this.initModeValue();
          this.accessGroupUpdateInProgress = false;
          this.amanoOneAccessGroups = this.accessGroupLists.availableGroups;
          this.amanoOneAccessGroupsMemberOf = this.accessGroupLists.membershipGroups;
        },
      });
  }

  onCredentialTypeChange(event: any) {
    this.credentialTypeSettingValue = event.value;
    this.credentialTypeDisabled = this.credentialTypeSettingValue == 'barcode';

    const credentialId: number =
      this.credentialTypeList.find((ct) => ct.credentialTypeName === this.credentialTypeSettingValue)
        ?.credentialTypeId || 0;

    this.getCredentialSystemCodesListForLotNumber(this.#_appGeneralStateService.currentLotNumber, credentialId);
    this.evaluateCredentialSaveStatus();
  }

  onCredentialCodeChange(event: any) {
    this.credentialCodeSettingValue = event.value;
    this.evaluateCredentialSaveStatus();
  }
}
